import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "loading", "clear"]
  static values = { 
    url: String,
    minLength: { type: Number, default: 2 },
    delay: { type: Number, default: 300 }
  }
  
  connect() {
    this.timeout = null
    this.abortController = null
  }
  
  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    if (this.abortController) {
      this.abortController.abort()
    }
  }
  
  search() {
    const query = this.inputTarget.value.trim()
    
    // Clear timeout if user is still typing
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    
    // Show/hide clear button
    if (this.hasClearTarget) {
      this.clearTarget.classList.toggle("hidden", query.length === 0)
    }
    
    // Check minimum length
    if (query.length < this.minLengthValue) {
      this.clearResults()
      return
    }
    
    // Show loading state
    this.showLoading()
    
    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, this.delayValue)
  }
  
  async performSearch(query) {
    // Cancel previous request if still pending
    if (this.abortController) {
      this.abortController.abort()
    }
    
    // Create new abort controller for this request
    this.abortController = new AbortController()
    
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)
      
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          "Accept": "text/vnd.turbo-stream.html, text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const contentType = response.headers.get("content-type")
      
      if (contentType.includes("text/vnd.turbo-stream.html")) {
        // Handle Turbo Stream response
        const text = await response.text()
        Turbo.renderStreamMessage(text)
      } else if (contentType.includes("text/html")) {
        // Handle regular HTML response
        const html = await response.text()
        this.updateResults(html)
      } else {
        // Handle JSON response
        const data = await response.json()
        this.handleJsonResponse(data)
      }
      
      this.hideLoading()
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error)
        this.showError()
      }
    }
  }
  
  updateResults(html) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = html
      this.resultsTarget.classList.remove("hidden")
    }
  }
  
  handleJsonResponse(data) {
    // Override this method in subclasses to handle JSON responses
    console.log('Search results:', data)
  }
  
  clearSearch() {
    this.inputTarget.value = ""
    this.clearResults()
    
    if (this.hasClearTarget) {
      this.clearTarget.classList.add("hidden")
    }
    
    // Focus back on input
    this.inputTarget.focus()
  }
  
  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
    }
    
    this.hideLoading()
  }
  
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
  }
  
  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }
  
  showError() {
    this.hideLoading()
    
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `
        <div class="p-4 text-center text-red-400">
          <p>An error occurred while searching. Please try again.</p>
        </div>
      `
      this.resultsTarget.classList.remove("hidden")
    }
  }
  
  // Handle clicking outside to close results
  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.clearResults()
    }
  }
  
  // Handle keyboard navigation
  handleKeydown(event) {
    switch(event.key) {
      case 'Escape':
        this.clearResults()
        this.inputTarget.blur()
        break
      case 'ArrowDown':
        event.preventDefault()
        this.focusNextResult()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.focusPreviousResult()
        break
    }
  }
  
  focusNextResult() {
    // Implement keyboard navigation for search results
    const focusable = this.resultsTarget.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])')
    const currentIndex = Array.from(focusable).indexOf(document.activeElement)
    
    if (currentIndex < focusable.length - 1) {
      focusable[currentIndex + 1].focus()
    }
  }
  
  focusPreviousResult() {
    const focusable = this.resultsTarget.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])')
    const currentIndex = Array.from(focusable).indexOf(document.activeElement)
    
    if (currentIndex > 0) {
      focusable[currentIndex - 1].focus()
    } else {
      this.inputTarget.focus()
    }
  }
} 